import { strings } from "./i18n";
import type { DayTrip, Lang, PartnerMapContent, Tour, WeekEvent, WeekProgram } from "./types";

/**
 * The week program as plain functions of a date: which days there are, what is on each, and the text of the
 * WhatsApp sign-up. `today` is passed in, never read, so the server's render and the client's cannot disagree
 * about it and a test can say what day it is.
 */

/** the English day names: a page in another language reads `strings(lang).days` */
export const DAYS = strings("en").days;

/** 0 = Monday ... 6 = Sunday */
export const weekdayIndex = (date: Date): number => (date.getDay() + 6) % 7;

/** the seven days in the order the strip shows them, today first */
export const dayOrder = (todayIdx: number): number[] => Array.from({ length: 7 }, (_, k) => (todayIdx + k) % 7);

/** the next date this weekday falls on, today counting */
function nextDate(today: Date, day: number): Date {
	const date = new Date(today);
	date.setDate(date.getDate() + ((day - weekdayIndex(today) + 7) % 7));
	return date;
}

/** "2026-10-31", in the visitor's own time like the rest of the page's dates */
export const isoDate = (date: Date): string =>
	`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

/**
 * What is on a day: the weekly events on that weekday and, when `today` is known, the one-night events (`date`) that fall on
 * the date the day's chip stands for. Those come in by their start, among the weekly ones; without `today` they cannot be placed.
 */
export const eventsOn = (program: WeekProgram | undefined, day: number, today?: Date | null): WeekEvent[] => {
	const on = program?.events.filter((e) => (e.date ? !!today && e.date === isoDate(nextDate(today, day)) : e.days.includes(day))) ?? [];
	// An event with no start sorts last.
	return on.some((e) => e.date) ? [...on].sort((a, b) => (firstTime(a) || "99:99").localeCompare(firstTime(b) || "99:99")) : on;
};

/** the one-night events from today on, soonest first: the "Coming up" strip */
export const upcoming = (program: WeekProgram | undefined, today: Date): WeekEvent[] => {
	const from = isoDate(today);
	return (program?.events.filter((e) => e.date && e.date >= from) ?? []).sort((a, b) => `${a.date} ${firstTime(a)}`.localeCompare(`${b.date} ${firstTime(b)}`));
};

export const tripsOn = (program: WeekProgram | undefined, day: number): DayTrip[] => program?.dayTrips?.trips.filter((t) => t.days.includes(day)) ?? [];

/** the tours on a day; a tour that names no days is offered on every one */
export const toursOn = (program: WeekProgram | undefined, day: number): Tour[] => program?.tours?.filter((t) => !t.days.length || t.days.includes(day)) ?? [];

/** a row of the tours list: a tour on its own, or the variants that share a bundle (`group`) */
export type TourRow = { tour: Tour } | { group: string; tours: Tour[] };

/**
 * The tours as rows: the variants of a bundle collapse into one row, where its first variant was; the rest keep their order.
 * A bundle of one variant is not one: it stays a row of its own.
 */
export function bundleTours(tours: Tour[]): TourRow[] {
	const variantsOf = new Map<string, Tour[]>();
	for (const tour of tours) if (tour.group) variantsOf.set(tour.group, [...(variantsOf.get(tour.group) ?? []), tour]);
	return tours.flatMap((tour): TourRow[] => {
		const group = tour.group ?? "";
		const variants = variantsOf.get(group) ?? [];
		if (variants.length < 2) return [{ tour }];
		return variants[0] === tour ? [{ group, tours: variants }] : [];
	});
}

const DURATION_UNITS: Record<string, [string, string]> = { d: ["day", "days"], h: ["hr", "hrs"], m: ["min", "min"] };

/** "4 DAYS", "3-4 hrs", "1 HR", "30 MIN": a range (or one number) and its unit, by the unit's first letter; null for anything else, like "—" */
function parseDuration(text: string): { lo: number; hi: number; unit: string } | null {
	const match = /^\s*(\d+(?:\.\d+)?)(?:\s*[-–]\s*(\d+(?:\.\d+)?))?\s*(days?|hrs?|hours?|mins?|minutes?)\b/i.exec(text);
	if (!match) return null;
	const [lo, hi] = [Number(match[1]), Number(match[2] ?? match[1])];
	return { lo: Math.min(lo, hi), hi: Math.max(lo, hi), unit: match[3][0].toLowerCase() };
}

/** What a bundle's variants last, as one phrase: "3–4 days", "4 hrs". "" when their units differ or none of them says. */
export function durationSummary(tours: Tour[]): string {
	const spans = tours.flatMap((tour) => parseDuration(tour.duration) ?? []);
	if (!spans.length || spans.some(({ unit }) => unit !== spans[0].unit)) return "";
	const lo = Math.min(...spans.map((span) => span.lo));
	const hi = Math.max(...spans.map((span) => span.hi));
	const [one, many] = DURATION_UNITS[spans[0].unit];
	return `${lo === hi ? lo : `${lo}–${hi}`} ${hi === 1 ? one : many}`;
}

/** the day of the month, for the strip's chip */
export const shortDate = (today: Date, day: number): number => nextDate(today, day).getDate();

/** "Sat 31 Oct", for the day of a one-night event */
export const niceDate = (iso: string, lang: Lang = "en"): string =>
	new Date(`${iso}T12:00`).toLocaleDateString(strings(lang).locale, { weekday: "short", day: "numeric", month: "short" });

/** "Tuesday 21 October" */
export const dateOf = (today: Date, day: number, lang: Lang = "en"): string =>
	nextDate(today, day).toLocaleDateString(strings(lang).locale, { weekday: "long", day: "numeric", month: "long" });

/** the first start of an event: "11:00 · 13:00" starts at 11:00 */
export const firstTime = (event: WeekEvent): string => event.time.split(" · ")[0];

/** the starts after the first, as a phrase: "20:00 and 22:00" for "18:00 · 20:00 · 22:00", nothing for an event with one start */
export const laterTimes = (event: WeekEvent, lang: Lang = "en"): string =>
	new Intl.ListFormat(strings(lang).locale, { type: "conjunction" }).format(event.time.split(" · ").slice(1));

/** what the "Tonight on ..." line says for this day: the `tonight` phrases of today's events */
export const tonightOn = (program: WeekProgram | undefined, day: number, today?: Date | null): string[] =>
	eventsOn(program, day, today).flatMap((e) => (e.tonight ? [e.tonight] : []));

const whatsappUrl = (number: string, text: string): string => `https://wa.me/${number}?text=${encodeURIComponent(text)}`;

/** the "WhatsApp" button of the contact row: an opening line and nothing more */
export const greetingUrl = ({ partner, lang }: PartnerMapContent): string => whatsappUrl(partner.whatsapp, strings(lang).greeting(partner.shortName));

/** the sign-up button of an event: the message names the event, the day it is on and when it starts */
export const signupUrl = ({ partner, lang }: PartnerMapContent, event: WeekEvent, today: Date, day: number): string =>
	whatsappUrl(partner.whatsapp, strings(lang).signupMessage(partner.shortName, event.title.toLowerCase(), dateOf(today, day, lang), firstTime(event)));
